/* eslint-disable func-call-spacing */
/* eslint-disable no-spaced-func */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { currencyFormat } from 'helpers/currencyFormat';

// Constants
import { REPORT_GROUPINGS } from 'constants/sellingTerminals';

// Types
import { PaymentInterface } from 'store/ducks/employeesSales/types';
import { ApplicationState } from 'store';

// Styles
import useStyle from './paymentNavigation.styles';

interface PaymentNavigationProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  groupings: Array<string>;
  isHistoryDatails?: boolean;
  paymentsList?: PaymentInterface[];
  setPaymentOrder?: (o: Order) => void;
  setPaymentOrderBy?: (o: string) => void;
}

type Props = PaymentNavigationProps;

const PaymentNavigation: React.FC<Props> = ({
  setTableReference,
  setTableCSV,
  groupings,
  isHistoryDatails,
  paymentsList,
  setPaymentOrder,
  setPaymentOrderBy,
}) => {
  const classes = useStyle();

  const employeesList = useSelector(
    (state: ApplicationState) => state.employees.employeesList,
  );

  // data formatting
  const initialPaymentsList = useSelector((state: ApplicationState) => {
    const list =
      paymentsList && paymentsList.length
        ? paymentsList
        : state.employeesSales.paymentsList;
    if (list.length === 0) return [];
    // if there is no kind of grouping
    if (groupings.length === 0) {
      return list.map((p) => ({
        id: p.id,
        amount: p.amount,
        createdAt: p.createdAt,
        description: p.description,
        receivedBy: p.TransportSupervisor && p.TransportSupervisor.name,
        transportOperatorId: p.transportOperatorId,
      }));
    }
    // if there is any kind of grouping
    return list.map(({ employeeId, count, totalValue, usedDate }) => {
      const ret = { count, totalValue };
      if (employeeId) {
        Object.assign(ret, ret, { employeeId });
      }
      if (usedDate !== undefined) {
        Object.assign(ret, ret, { usedDate });
      }
      return ret;
    });
  });

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('amount');
  const [tableColumns, setTableColumns] = useState<
    Array<{ key: string; name: string }>
  >([]);

  // defining table structure
  useEffect(() => {
    if (groupings.length === 0) {
      setTableColumns([
        { key: 'amount', name: 'Valor' },
        { key: 'createdAt', name: 'Data e Hora' },
        { key: 'description', name: 'Descrição' },
        { key: 'receivedBy', name: 'Recebido por' },
      ]);
    } else {
      let columns: Array<{ key: string; name: string }> = [];
      if (groupings.includes(REPORT_GROUPINGS.PAYMENT.EMPLOYEE)) {
        columns = columns.concat([{ key: 'employeeId', name: 'Motorista' }]);
      }
      if (groupings.includes(REPORT_GROUPINGS.PAYMENT.USED_DATE)) {
        columns = columns.concat([{ key: 'usedDate', name: 'Data' }]);
      }
      columns = columns.concat([
        { key: 'totalValue', name: 'Valor total' },
        { key: 'count', name: 'Quantidade' },
      ]);
      setTableColumns(columns);
    }
  }, [groupings]);

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (tableColumns.length > 0 && initialPaymentsList.length > 0) {
      csv = tableColumns.reduce((a, c) => `${a}${a ? ';' : ''}${c.name}`, '');
      if (transportOperatorSelected.type === 1) csv += ';Operação';

      let totalValue = 0;
      stableSort(
        stableSort(initialPaymentsList, getComparator('desc', 'createdAt')),
        getComparator('asc', 'amount'),
      ).forEach((p: { [x: string]: any }) => {
        if (p.amount) {
          totalValue += p.amount;
        } else if (p.totalValue) {
          totalValue += p.totalValue;
        }

        csv += '\n';
        tableColumns.forEach((c) => {
          csv += `${
            c.key === 'amount'
              ? currencyFormat({ value: Number(p[c.key]) || 0 })
              : c.key === 'createdAt' || c.key === 'usedDate'
              ? formatDateTime(p[c.key])
              : p[c.key] || ''
          };`;
        });
        if (transportOperatorSelected.type === 1) {
          csv +=
            transportOperatorsList.find((e) => e.id === p?.transportOperatorId)
              ?.name || '---';
        }
      });

      csv += `\n`;
      csv += `${totalValue};Total`;
    }
    setTableCSV(csv);
    // eslint-disable-next-line
  }, [tableColumns, initialPaymentsList]);

  // Formatters

  const formatDateTime = (date: Date) =>
    date && new Date(date).toLocaleString('pt-BR');

  const formatAmount = (amount: number | string) =>
    amount &&
    `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  // Field to Formatter Mapping
  const fieldFormatting = new Map<string, (arg: any) => any>([
    ['createdAt', formatDateTime],
    ['amount', formatAmount],
    ['totalValue', formatAmount],
  ]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    if (setPaymentOrder) setPaymentOrder(isAsc ? 'desc' : 'asc');
    if (setPaymentOrderBy) setPaymentOrderBy(property);
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {tableColumns.length > 0 &&
              tableColumns.map((col) => (
                <TableCell
                  className={isHistoryDatails ? classes.firstTd : ''}
                  key={col.key}
                  variant='head'
                  align='center'
                  sortDirection={orderBy === col.key ? order : false}>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, col.key)}>
                    {col.name}
                    {orderBy === col.key ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {initialPaymentsList.length > 0 &&
            stableSort(initialPaymentsList, getComparator(order, orderBy)).map(
              (payment, index) => (
                <TableRow>
                  {tableColumns.map((col) => {
                    const formatter = fieldFormatting.get(col.key);
                    return (
                      <TableCell key={col.key} align='center'>
                        {formatter
                          ? formatter(payment[col.key])
                          : payment[col.key] === payment.employeeId
                          ? employeesList.find(
                              (employee) => employee.id === payment.employeeId,
                            )?.name
                          : payment[col.key]}
                      </TableCell>
                    );
                  })}
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === payment?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { PaymentNavigation };
export default PaymentNavigation;
